import { useCallback } from 'react';
import { getDramaService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type {
  CreateDramaRequest,
  Drama,
  DramaDetailResponse,
  DramasQueryParams,
  UpdateDramaRequest,
} from '../types/api/drama';
import type {
  PreviewImageResponse,
  PreviewImageMetadataRequest,
  PreviewImageUpdateRequest,
} from '../types/api/movie';

/**
 * Custom hook for series management (dramaType = 'SERIES')
 *
 * Integrates DramaService with the DataTable component and page-level forms.
 * Handles data transformation between API and table formats.
 */
export function useSeries() {
  const dramaService = getDramaService();

  /**
   * Fetch function compatible with DataTable.
   *
   * Converts DataTable's 1-based page index to backend's 0-based index and
   * adds a fixed `dramaType=series` filter so this hook always returns series.
   */
  const fetchSeries: FetchDataFn<Drama> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Drama>> => {
      try {
        const apiParams: DramasQueryParams = {
          page: params.page, // backend now 1-based
          size: params.pageSize,
          dramaType: 'series',
        };

        if (params.search) {
          apiParams.keyword = params.search;
        }

        // Extract accessLevel filter if present
        if (params.filters?.accessLevel?.value) {
          const accessLevelValue = params.filters.accessLevel.value;
          if (accessLevelValue && accessLevelValue !== '') {
            apiParams.accessLevel = accessLevelValue;
          }
        }

        // Extract contentRegion filter if present ("myanmar" | "international")
        if (params.filters?.contentRegion?.value) {
          const contentRegionValue = params.filters.contentRegion.value;
          if (contentRegionValue === 'myanmar' || contentRegionValue === 'international') {
            apiParams.contentRegion = contentRegionValue;
          }
        }

        const response = await dramaService.getDramas(apiParams);

        return {
          data: response.items,
          pagination: {
            page: response.page,
            pageSize: response.size,
            total: response.totalItems,
            totalPages: response.totalPages,
            hasNext: response.hasNext,
            hasPrev: response.hasPrevious,
          },
        };
      } catch (error) {
        console.error('Failed to fetch series:', error);
        throw new Error('Failed to load series. Please try again.');
      }
    },
    [dramaService]
  );

  return {
    fetchSeries,
  };
}

/**
 * Hook for series actions (CRUD).
 *
 * Provides high-level functions tailored to series (dramaType = 'SERIES').
 */
export function useSeriesActions() {
  const dramaService = getDramaService();

  const createSeriesWithThumbnail = useCallback(
    async (
      data: Omit<CreateDramaRequest, 'dramaType'>,
      thumbnailFile?: File
    ): Promise<Drama> => {
      try {
        const payload: CreateDramaRequest = {
          ...data,
          dramaType: 'SERIES',
        };
        return await dramaService.createDramaWithThumbnail(payload, thumbnailFile);
      } catch (error) {
        console.error('Failed to create series with thumbnail:', error);
        throw error;
      }
    },
    [dramaService]
  );

  const createSeriesWithJson = useCallback(
    async (data: Omit<CreateDramaRequest, 'dramaType'>): Promise<Drama> => {
      try {
        const payload: CreateDramaRequest = {
          ...data,
          dramaType: 'SERIES',
        };
        return await dramaService.createDramaWithJson(payload);
      } catch (error) {
        console.error('Failed to create series (JSON):', error);
        throw error;
      }
    },
    [dramaService]
  );

  const updateSeries = useCallback(
    async (seriesId: string, update: UpdateDramaRequest): Promise<Drama> => {
      try {
        const payload: UpdateDramaRequest = {
          ...update,
          dramaType: 'SERIES',
        };
        return await dramaService.updateDrama(seriesId, payload);
      } catch (error) {
        console.error('Failed to update series:', error);
        throw error;
      }
    },
    [dramaService]
  );

  const softDeleteSeries = useCallback(
    async (seriesId: string): Promise<void> => {
      try {
        await dramaService.softDeleteDrama(seriesId);
      } catch (error) {
        console.error('Failed to delete series:', error);
        throw error;
      }
    },
    [dramaService]
  );

  const permanentDeleteSeries = useCallback(
    async (seriesId: string): Promise<void> => {
      try {
        await dramaService.permanentDeleteDrama(seriesId);
      } catch (error) {
        console.error('Failed to permanently delete series:', error);
        throw error;
      }
    },
    [dramaService]
  );

  const getSeries = useCallback(
    async (seriesId: string): Promise<DramaDetailResponse> => {
      try {
        return await dramaService.getDrama(seriesId);
      } catch (error) {
        console.error('Failed to fetch series details:', error);
        throw error;
      }
    },
    [dramaService]
  );

  const uploadPreviewImages = useCallback(
    async (
      dramaId: string,
      files: File[],
      metadata?: PreviewImageMetadataRequest[]
    ): Promise<PreviewImageResponse[]> => {
      try {
        const batchMetadata = metadata ? { metadata } : undefined;
        return await dramaService.uploadPreviewImages(dramaId, files, batchMetadata);
      } catch (error) {
        console.error('Failed to upload drama preview images:', error);
        throw error;
      }
    },
    [dramaService]
  );

  const updatePreviewImageMetadata = useCallback(
    async (
      dramaId: string,
      previewImageId: string,
      payload: PreviewImageUpdateRequest
    ): Promise<PreviewImageResponse> => {
      try {
        return await dramaService.updatePreviewImageMetadata(dramaId, previewImageId, payload);
      } catch (error) {
        console.error('Failed to update drama preview image metadata:', error);
        throw error;
      }
    },
    [dramaService]
  );

  const updatePreviewImageWithFile = useCallback(
    async (
      dramaId: string,
      previewImageId: string,
      payload: PreviewImageUpdateRequest,
      file?: File
    ): Promise<PreviewImageResponse> => {
      try {
        return await dramaService.updatePreviewImageWithFile(dramaId, previewImageId, payload, file);
      } catch (error) {
        console.error('Failed to update drama preview image with file:', error);
        throw error;
      }
    },
    [dramaService]
  );

  return {
    createSeriesWithThumbnail,
    createSeriesWithJson,
    updateSeries,
    softDeleteSeries,
    permanentDeleteSeries,
    getSeries,
    uploadPreviewImages,
    updatePreviewImageMetadata,
    updatePreviewImageWithFile,
  };
}


